import {parseISO, getMonth, getYear} from 'date-fns'
import {findLastIndex} from 'lodash'
import cn from 'clsx'
import {Space} from 'antd'

import styles from './ProjectScheduleList.module.scss'
import {
  Box,
  Text,
  Block,
  ListHeader,
  Circle,
  CentsMoney,
  VerticalDivider,
  MoneyBanknotesRotatedSecondIcon,
  MoneyBanknoteExchangeIcon,
  RotateArrowThirdIcon,
  RotateArrowFourthIcon,
} from 'common/components'
import {
  formatDate,
  formatMoney,
  maybePluralize,
  countPercentage,
  totalNumberFromArray,
  isItemInDrawdownColumn,
  _differenceInCalendarDays,
} from 'common/helpers'
import {
  isScheduleItemRepayment,
  TRANSACTION_NAME,
  Transaction,
} from '..'

interface ScheduleMonthProps {
  scheduleMonth?: ScheduleMonthInterface
}

interface BarProps {
  estimated?: boolean
  request?: any
  project: any
  schedule: any[]
}

interface EstimatedStatProps {
  label: string
  percent: number
  money: number
}

interface EstimatedStatsProps {
  project: any
  totalInterestFees: number
}

interface ScheduleItemProps {
  estimatedMonth?: boolean
  item: any
}

interface ScheduleMonthInterface {
  month: number
  year: number
  past: boolean
  items: any[]
}

export default function ProjectScheduleList({request, project, schedule}: any) {
  return (
    <>
      <Head />
      <ul className={styles.list}>
        {getScheduleMonths(schedule).map((scheduleMonth) => (
          <ScheduleMonth
            key={`${scheduleMonth.month}${scheduleMonth.year}`}
            scheduleMonth={scheduleMonth}
          />
        ))}
      </ul>
      <Bar request={request} project={project} schedule={schedule} />
      <Bar request={request} estimated project={project} schedule={schedule} />
    </>
  )
}

const Head = () => (
  <ListHeader className={styles.grid}>
    <Box align="right">Date</Box>
    <div>Reference</div>
    <Box align="right">Interest, fees</Box>
    <Box align="right">Drawdowns</Box>
    <Box align="right">Repayments</Box>
    <Box align="right">Balance</Box>
  </ListHeader>
)

const ScheduleMonth = ({scheduleMonth}: ScheduleMonthProps) => {
  if (scheduleMonth.past)
    return (
      <Block as="li" className={styles.month} type="grey">
        {scheduleMonth.items.map((item, index) => (
          <ScheduleItem key={index} item={item} />
        ))}
      </Block>
    )

  return (
    <li className={cn(styles.month, styles.month_estimated)}>
      {scheduleMonth.items.map((item, index) => (
        <ScheduleItem key={index} estimatedMonth item={item} />
      ))}
    </li>
  )
}

const ScheduleItem = ({estimatedMonth, item}: ScheduleItemProps) => {
  const isDrawdown = isItemInDrawdownColumn(item)
  const isRepayment = isScheduleItemRepayment(item)

  return (
    <div
      className={cn(styles.grid, item.lastTransaction && styles.grid_selected)}
    >
      <div className={styles.date}>
        <div>
          {!estimatedMonth && (
            <Circle
              hole={!item.transaction}
              color={isRepayment ? 'darkGreen' : undefined}
            />
          )}
        </div>
        <p>{formatDate(item.date, 'fifth')}</p>
      </div>
      <div className={styles.reference}>
        <Space>
          {isDrawdown ? (
            <RotateArrowThirdIcon />
          ) : isRepayment && item.type !== 'application-fee' ? (
            <RotateArrowFourthIcon />
          ) : null}
          <p className={styles.type}>
            {!item.transaction && (isDrawdown || isRepayment) && 'Est. '}
            {TRANSACTION_NAME[item.type as Transaction]}
          </p>
        </Space>
        {item.notes && (
          <Text size="xs" color="grey" ml={12}>
            {item.notes}
          </Text>
        )}
      </div>
      <Text align="right">
        {!isDrawdown &&
          !isRepayment &&
          formatMoney(item.value, {
            prefix: false,
            cents: true,
          })}
      </Text>
      <Text align="right" color={estimatedMonth ? 'turquoizeDeep' : undefined}>
        {isDrawdown &&
          formatMoney(item.value, {
            prefix: false,
            cents: true,
          })}
      </Text>
      <Text align="right">
        {isRepayment &&
          formatMoney(Math.abs(item.value), {
            prefix: false,
            cents: true,
            parentheses: true,
          })}
      </Text>
      <Box
        align="right"
        size="lg"
        weight={item.lastTransaction ? 'bold' : 'medium'}
      >
        <CentsMoney
          as="span"
          value={Math.abs(item.balanceAfter)}
          parentheses={item.balanceAfter < 0}
        />
      </Box>
    </div>
  )
}

const EstimatedStats = ({project, totalInterestFees}: EstimatedStatsProps) => {
  const fees = project.totalFees + project.estFees

  return (
    <div className={styles.estimatedStats}>
      <EstimatedStat
        label="interest"
        percent={countPercentage(project.totalInterest, totalInterestFees)}
        money={project.totalInterest}
      />
      <VerticalDivider className={styles.divider} margin="small" />
      <EstimatedStat
        label="fees"
        percent={countPercentage(fees, totalInterestFees)}
        money={fees}
      />
    </div>
  )
}

const EstimatedStat = ({label, percent, money}: EstimatedStatProps) => (
  <Box align="center">
    <Text size="xs" mb={4}>
      {label}
    </Text>
    <Text color="turquoize" weight="demiBold" mb={4}>
      {percent}%
    </Text>
    <Text size="xs" color="white50">
      {formatMoney(money, {cents: true})}
    </Text>
  </Box>
)

const Bar = ({request, estimated, project, schedule}: BarProps) => {
  const today = new Date()
  const maturityDate = parseISO(project.maturityDate)
  const totalDays = estimated
    ? _differenceInCalendarDays(maturityDate, parseISO(project.openDate))
    : _differenceInCalendarDays(today, parseISO(project.openDate))
  // console.log(request)
  // console.log(project)
  const totalInterestFees = estimated
    ? project.totalIncome // project.cashflowSum
    : project.currentIncome // project.currentInterest + project.currentFees + project.estFees
  const totalWithdrawn = estimated
    ? totalNumberFromArray(
        schedule.filter((item) => isItemInDrawdownColumn(item)),
        'value'
      )
    : project.withdrawn
  const repaidItems = estimated
    ? schedule.filter((item) => isScheduleItemRepayment(item))
    : null
  const totalRepaid = estimated
    ? Math.abs(totalNumberFromArray(repaidItems, 'value'))
    : project.repaid
  let totalBalance = project.balance
  if (estimated) {
    const positiveScheduleItems = schedule.filter((item) => item.value >= 0 || item.type === 'application-fee')
    totalBalance = totalNumberFromArray(positiveScheduleItems, 'value')
  }
  const facilitLimit1 = request
    ? request.facilityLimit1
    : project.facilityLimit1
  const facilitLimit2 = request
    ? request.facilityLimit2
    : project.facilityLimit2
  const isOverLoanLimit = estimated ? totalWithdrawn > facilitLimit1 : undefined
  const isOverFacilityLimit = estimated
    ? totalBalance > facilitLimit2
    : undefined

  return (
    <>
      <Block className={cn(styles.grid, styles.grid_bar)} type="black">
        <div className={styles.barColumn}>
          <div>
            <p className={styles.label}>
              {estimated
                ? 'estimated total'
                : `current total, ${formatDate(today, 'third')}`}
            </p>
            <Text size="xl">
              {totalDays === 0
                ? 'today'
                : `${maybePluralize(Math.abs(totalDays), 'day')}${
                    totalDays < 0 ? ` to open` : ''
                  }`}
            </Text>
            {estimated && (
              <p className={styles.subLabel}>
                on maturity day, {formatDate(maturityDate, 'third')}
              </p>
            )}
          </div>
          {estimated && (
            <div className={styles.barStats}>
              <div className={styles.irr}>
                <p className={styles.label}>total IRR</p>
                <Space size={6}>
                  <MoneyBanknoteExchangeIcon
                    className={styles.atmo}
                    height={16}
                  />
                  <Text size="xl">{project.xirr}%</Text>
                </Space>
              </div>
              <EstimatedStats
                project={project}
                totalInterestFees={totalInterestFees}
              />
            </div>
          )}
        </div>
        <Box align="right">
          <p className={styles.label}>interest & fees</p>
          <CentsMoney value={totalInterestFees} />
        </Box>
        <Box align="right">
          <p className={styles.label}>withdrawn</p>
          <Text color={isOverLoanLimit ? 'redHalf' : undefined}>
            {formatMoney(totalWithdrawn, {cents: true})}
          </Text>
          {estimated && (
            <p className={styles.subLabel} style={{
              whiteSpace: 'nowrap'
            }}>
              facility limit 1 {formatMoney(project.facilityLimit1, {cents: true})}
            </p>
          )}
        </Box>
        <Box align="right">
          <p className={styles.label}>repaid</p>
          <CentsMoney value={totalRepaid} />
        </Box>
        <Box align="right">
          <p className={styles.label}>balance</p>
          <Space size={6}>
            <MoneyBanknotesRotatedSecondIcon className={styles.greenLight} />
            <CentsMoney
              value={totalBalance}
              size="xl"
              color={isOverFacilityLimit ? 'redHalf' : undefined}
            />
          </Space>
          {estimated && (
            <p className={styles.subLabel}>
              facility limit 2:{' '}
              {formatMoney(
                request ? request.facilityLimit2 : project.facilityLimit2,
                {cents: true}
              )}
            </p>
          )}
        </Box>
      </Block>
      {(isOverLoanLimit || isOverFacilityLimit) && (
        <Text
          align="right"
          size="xs"
          weight="demiBold"
          color="redError"
          mt={8}
          mr={24}
        >
          *estimated total is higher than{' '}
          {getErrorText(isOverLoanLimit, isOverFacilityLimit)}
        </Text>
      )}
    </>
  )
}

const getScheduleMonths = (schedule: any[]): ScheduleMonthInterface[] => {
  const today = new Date()
  const newSchedule = schedule.slice()
  const lastTransactedItemIndex = findLastIndex(
    schedule,
    (item) => item.transaction
  )
  const lastTransactedItem = schedule[lastTransactedItemIndex]
  if (lastTransactedItem) {
    newSchedule[lastTransactedItemIndex] = {
      ...lastTransactedItem,
      lastTransaction: true,
    }
  }
  return newSchedule.reduce((arr: ScheduleMonthInterface[], item: any) => {
    const date = parseISO(item.date)
    const month = getMonth(date)
    const year = getYear(date)
    const scheduleMonth = arr.find(
      (arrItem) => arrItem.month === month && arrItem.year === year
    )
    if (scheduleMonth) {
      scheduleMonth.items.push(item)
    } else {
      arr.push({
        month,
        year,
        past: date < today,
        items: [item],
      })
    }
    return arr
  }, [])
}

const getErrorText = (
  isOverLoanLimit: boolean,
  isOverFacilityLimit: boolean
): string => {
  const errorNames: string[] = []
  if (isOverLoanLimit) errorNames.push('loan amount')
  if (isOverFacilityLimit) errorNames.push('facility limit')
  return errorNames.join(' & ')
}
